import { HeadersOptionsType } from "@/types/ModuleOptionsType";
import { ParentsType } from "../../../wp/getParentPages";
import styles from "./TeamHeader.module.scss";
import HeaderBase from "@/components/_headers/HeaderBase/HeaderBase";
import Split from "@/components/_primitives/Split/Split";
import { bulmaColumnsToWidth, columnSize } from "@/utils/layout/columnSize";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Post } from "@/wp/types/client";
import { ImageType, OptionsType } from "@/types/baseTypes";
import { passACFLink } from "@/utils/passACFLink";
import { ReactSVG } from "react-svg";
import { MediaItem } from "@/components/MediaItem/MediaItem";
import { CustomLink } from "@/components/CustomLink";
import { Headline } from "@/components/Headline/Headline";

export interface TeamHeaderProps {
  post: Post<"team">;
  headerOptions?: HeadersOptionsType["header_team"];
  parents: ParentsType;
  fallbackImg?: ImageType | false;
  socialMediaThemeOptions: OptionsType["acf"]["social_media"];
}

export const TeamHeader = ({
  post,
  headerOptions,
  parents,
  fallbackImg,
  socialMediaThemeOptions,
}: TeamHeaderProps) => {
  const header = post.acf.header_team;
  const { background_color_class, section_margin_class, image } = header;

  const { splitheader, show_fallback_image, show, breadcrumb_position } =
    headerOptions ?? {};

  const {
    image_height: splitheaderImageHeight,
    media_format: splitheaderMediaFormat,
    header_width: splitheaderWidth,
    text_vertical_align: splitheaderTextVerticalAlign,
    text_width_with_spacer: splitheaderTextWidthWithSpacer,
  } = splitheader ?? {};

  const splitHeaderDirection = "row-reverse";

  const breadcrumbsJSX = (
    <div className="nav-oriented-breadcrumbs">
      <Breadcrumbs
        parents={parents}
        post={post}
        breadcrumbPositon={
          breadcrumb_position
            ? breadcrumb_position
            : "is-justify-content-flex-start"
        }
      />
    </div>
  );

  const mediaJSX = (className: string = "") => (
    <MediaItem
      image_desktop={image ?? (show_fallback_image && fallbackImg)}
      // mediaFormat={imageheaderMediaFormat}
      sizes="(max-width: 767px) 50vw, 100vw"
      priority={true}
      mediaFormat={splitheaderMediaFormat}
      className={`${className} ${splitheaderImageHeight === "image-height" ? "auto-height" : ""}`}
    />
  );

  const TeamHeaderSocials = () => {
    return (
      <div className={styles.socials}>
        {header.socials &&
          header.socials.map((social, key) => {
            const socialMediaPlatform =
              socialMediaThemeOptions.platforms &&
              socialMediaThemeOptions.platforms.find(
                (p) => p.name === social.social_media,
              );
            if (!socialMediaPlatform || !socialMediaPlatform?.icon?.url) return;
            return (
              <CustomLink
                {...passACFLink(social, "link")}
                target="_blank"
                key={key}
              >
                <ReactSVG
                  width={23}
                  height={23}
                  src={socialMediaPlatform.icon?.url}
                  wrapper={"svg"}
                  role="link"
                  beforeInjection={(svg) => {
                    svg.setAttribute("alt", `${socialMediaPlatform.name} Icon`);
                    svg.setAttribute(
                      "title",
                      `${socialMediaPlatform.name} Icon`,
                    );
                    svg.setAttribute(
                      "aria-label",
                      `${socialMediaPlatform.name} Icon`,
                    );
                  }}
                  aria-label={`${socialMediaPlatform.name} link`}
                />
              </CustomLink>
            );
          })}
      </div>
    );
  };

  const contentJSX = (
    <>
      <Headline
        headline={`${header.firstname} ${header.name}`}
        headline_html_tag="h1-pages"
        disableAnimation
      />
      {show?.includes("salutation") && (
        <p className={styles.salutation}>{header.salutation}</p>
      )}
      {mediaJSX("is-hidden-tablet mb-5")}
      {show?.includes("position") && <p>{header.salutation}</p>}
      {show?.includes("phone") && (
        <p className={styles.phone}>
          Tel.:{" "}
          <CustomLink href={`tel:${header.phone}`} target="_self">
            {header.phone}
          </CustomLink>
        </p>
      )}
      {show?.includes("mobile_phone") && (
        <p className={styles["mobile-phone"]}>
          Mobil:{" "}
          <CustomLink href={`tel:${header.mobile_phone}`} target="_self">
            {header.mobile_phone}
          </CustomLink>
        </p>
      )}
      {show?.includes("email") && <p>E-Mail: {header.email}</p>}
      {show?.includes("company_location") && <p>{header.company_location}</p>}
      {show?.includes("socials") && <TeamHeaderSocials />}
    </>
  );

  return (
    <HeaderBase
      className={`${background_color_class || ""} ${section_margin_class}`}
      contentFrame={
        splitheaderImageHeight === "screen-height-frameless"
          ? "frameless"
          : "narrow-frame"
      }
      headerFrame={"frameless"}
      contentHorizontalAlignment="center"
      height={
        splitheaderImageHeight === "screen-height-frameless"
          ? "screen-height"
          : "content-height"
      }
      narrowFrameColumns={
        splitheaderImageHeight === "screen-height-frameless"
          ? "is-12"
          : columnSize(splitheaderWidth ?? false) || "is-12"
      }
      arrangement="content-bottom"
      header={
        splitheaderImageHeight === "screen-height-frameless"
          ? undefined
          : breadcrumbsJSX
      }
      content={
        <Split
          first={mediaJSX("is-hidden-mobile")}
          firstClassName={`min-height-acceptor-desktop`}
          second={contentJSX}
          direction={splitHeaderDirection}
          firstVerticalAlignment={
            splitheaderImageHeight === "screen-height-frameless"
              ? "stretch"
              : "start"
          }
          secondVerticalAlignment={splitheaderTextVerticalAlign}
          secondOuterFractional={
            splitheaderImageHeight === "screen-height-frameless"
              ? breadcrumbsJSX
              : undefined
          }
          secondClassName="section-padding-y team-header-content"
          verticalBreapoints={"mobile"}
          sticky="first"
          stickyTop={
            splitheaderImageHeight === "screen-height-frameless"
              ? "var(--navbar-height)"
              : "calc(var(--navbar-height) + var(--section-padding-y))"
          }
          stickyMaxHeight="calc(100dvh - var(--navbar-height))"
          gap={"8.3333%"}
          gapOn="second"
          firstWidth={
            splitheaderImageHeight === "screen-height-frameless" ||
            !splitheaderTextWidthWithSpacer
              ? "50%"
              : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                  if (!curr.breakpoint) {
                    return prev;
                  }
                  return {
                    ...prev,
                    [curr.breakpoint]: `calc(100% - ${bulmaColumnsToWidth(curr.number_of_cols)})`,
                  };
                }, {})
          }
          secondWidth={
            splitheaderImageHeight === "screen-height-frameless" ||
            !splitheaderTextWidthWithSpacer
              ? "50%"
              : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                  if (!curr.breakpoint) {
                    return prev;
                  }
                  return {
                    ...prev,
                    [curr.breakpoint]: bulmaColumnsToWidth(curr.number_of_cols),
                  };
                }, {})
          }
          containerWidthLimit={{
            first: false,
            second: splitheaderImageHeight === "screen-height-frameless",
          }}
        />
      }
    />
  );
};
